import React, { useState } from 'react';
import PropTypes from 'prop-types';
import WorkspaceListPanel from './WorkspaceListPanel';
import GatewayStatusPanel from './GatewayStatusPanel';
import BackupListPanel from './BackupListPanel';
import TemplateGalleryPanel from './TemplateGalleryPanel';
import HealthDashboardPanel from './HealthDashboardPanel';

/**
 * Main Infrastructure Dashboard Container
 * Provides tabbed navigation between different Claw9 infrastructure sections
 */
function InfrastructureDashboard({ className = '' }) {
    const [activeTab, setActiveTab] = useState('workspaces');

    const tabs = [
        { id: 'workspaces', label: 'Workspaces', icon: 'folder' },
        { id: 'gateway', label: 'Gateway', icon: 'server' },
        { id: 'backups', label: 'Backups', icon: 'archive' },
        { id: 'templates', label: 'Templates', icon: 'template' },
        { id: 'health', label: 'Health', icon: 'heart' }
    ];

    const renderTabIcon = (icon) => {
        switch (icon) {
            case 'folder':
                return (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                );
            case 'server':
                return (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                    </svg>
                );
            case 'archive':
                return (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                );
            case 'template':
                return (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                );
            case 'heart':
                return (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                );
            default:
                return null;
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'workspaces':
                return <WorkspaceListPanel />;
            case 'gateway':
                return <GatewayStatusPanel />;
            case 'backups':
                return <BackupListPanel />;
            case 'templates':
                return <TemplateGalleryPanel />;
            case 'health':
                return <HealthDashboardPanel />;
            default:
                return <WorkspaceListPanel />;
        }
    };

    return (
        <div className={`flex flex-col h-full bg-gray-50 ${className}`}>
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <h1 className="text-2xl font-semibold text-gray-900">
                    Claw9 Infrastructure
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                    Manage workspaces, agents, and infrastructure components
                </p>
            </div>

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200">
                <nav className="flex px-6 space-x-8" aria-label="Tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`
                                flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                                transition-colors duration-150
                                ${activeTab === tab.id
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }
                            `}
                        >
                            {renderTabIcon(tab.icon)}
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                {renderContent()}
            </div>
        </div>
    );
}

InfrastructureDashboard.propTypes = {
    className: PropTypes.string
};

export default InfrastructureDashboard;
